<script setup lang="ts">
import { Head, useForm } from '@inertiajs/vue3'
import { computed, onMounted, ref, watch } from 'vue'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import Icon from '@/Components/Utils/Icon.vue'
import Modal from '@/Components/Utils/Modal.vue'
import PureSelectInfiniteScroll from '@/Components/Pure/PureSelectInfiniteScroll.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Checkbox, DatePicker, InputNumber, InputText, Select, Skeleton, Textarea } from 'primevue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import { useFormatTime } from '@/Composables/useFormatTime'

interface ProductOption {
    id: number | null
    key: string
    brand_product_id: number | null
    cs_stock_id: number | null
    label: string
    sku?: string
    category?: string
    print_type?: string
    print_label?: string
    price_tiers: { min_quantity: number, price: number }[]
    stock: { id: number, is_general: boolean, available: number } | null
}

interface BrandOption {
    id: number
    label: string
    phone?: string
    recipient_phone?: string
    address?: string
    customer_name?: string
    status?: string
    status_label?: string
    status_icon?: any
    special_note?: string
}

interface BrandFormData {
    products: ProductOption[]
    designs: { id: number, label: string, link: string, brand_product_id: number | null, link_updated_at: string | null }[]
    general_products: ProductOption[]
}

interface OrderLine {
    brand_product_id: number | null
    cs_stock_id: number | null
    quantity: number
    price: number | null
    note: string
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    initialBrand: BrandOption | null
    orderId?: number
    canEditReference?: boolean
    order: {
        reference: string
        brand_id: number
        recipient_phone: string | null
        invoice_date: string
        estimated_finished_at: string | null
        note: string | null
        shipping_cost: number
        shipping_subsidy: number
        additional_charge: number
        additional_charge_note: string | null
        design_type: string | null
        design_ids: number[]
        products: OrderLine[]
    } | null
    submitRoute: { name: string, parameters?: any[], method?: string }
}>()

const emptyLine = (): OrderLine => ({ brand_product_id: null, cs_stock_id: null, quantity: 1, price: null, note: '' })

const toDateInput = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const today = toDateInput(new Date())

const toDate = (value: string | null): Date | null => value ? new Date(`${value}T00:00:00`) : null

const form = useForm({
    ...(props.canEditReference && props.order ? { reference: props.order.reference } : {}),
    brand_id: props.order?.brand_id ?? props.initialBrand?.id ?? null,
    recipient_phone: props.order?.recipient_phone ?? '',
    invoice_date: props.order?.invoice_date ?? today,
    estimated_finished_at: props.order?.estimated_finished_at ?? '',
    note: props.order?.note ?? '',
    shipping_cost: props.order?.shipping_cost ?? 0,
    shipping_subsidy: props.order?.shipping_subsidy ?? 0,
    additional_charge: props.order?.additional_charge ?? 0,
    additional_charge_note: props.order?.additional_charge_note ?? '',
    design_type: props.order?.design_type ?? '',
    design_ids: (props.order?.design_ids ?? []) as number[],
    products: (props.order?.products?.length
        ? props.order.products
        : [emptyLine()]) as OrderLine[],
})

const selectedBrand = ref<BrandOption | null>(props.initialBrand)

const brandData = ref<BrandFormData>({ products: [], designs: [], general_products: [] })
const isLoadingBrandData = ref(false)
let latestBrandRequest = 0

const loadBrandData = async (): Promise<void> => {
    if (!form.brand_id) {
        brandData.value = { products: [], designs: [], general_products: [] }
        return
    }

    const requestId = ++latestBrandRequest
    isLoadingBrandData.value = true

    try {
        const response = await axios.get(route('kasir.orders.form.brand', {
            brand: form.brand_id,
            ...(props.orderId ? { order: props.orderId } : {}),
        }))
        if (requestId !== latestBrandRequest) return

        brandData.value = response.data
    } catch (error) {
        if (requestId !== latestBrandRequest) return
        notify({ title: 'Gagal memuat produk brand', text: 'Coba pilih ulang brand atau muat ulang halaman.', type: 'error' })
    } finally {
        if (requestId === latestBrandRequest) isLoadingBrandData.value = false
    }
}

onMounted(loadBrandData)

const findProduct = (brandProductId: number | null) =>
    brandData.value.products.find(product => product.id === brandProductId)

// General products are open to every brand, unless the brand already lists its own entry (e.g. for a brand price).
const productOptions = computed<ProductOption[]>(() => {
    const brandProducts = brandData.value.products
    const listedStockIds = brandProducts
        .map(product => product.stock?.id)
        .filter((stockId): stockId is number => typeof stockId === 'number')

    return [
        ...brandProducts,
        ...brandData.value.general_products.filter(product => product.cs_stock_id === null || !listedStockIds.includes(product.cs_stock_id)),
    ]
})

const lineKey = (line: OrderLine): string | null =>
    line.brand_product_id ? `bp-${line.brand_product_id}` : line.cs_stock_id ? `gs-${line.cs_stock_id}` : null

const findLineProduct = (line: OrderLine): ProductOption | undefined =>
    productOptions.value.find(product => product.key === lineKey(line))

const selectLineProduct = (line: OrderLine, key: string | null) => {
    const product = productOptions.value.find(option => option.key === key)
    line.brand_product_id = product?.brand_product_id ?? null
    line.cs_stock_id = product?.brand_product_id ? null : product?.cs_stock_id ?? null
}

const lineStock = (line: OrderLine) => findLineProduct(line)?.stock ?? null

const isOverStock = (line: OrderLine): boolean => {
    const stock = lineStock(line)
    if (!stock) return false

    const requested = form.products
        .filter(other => lineStock(other)?.id === stock.id)
        .reduce((sum, other) => sum + (other.quantity || 0), 0)

    return requested > stock.available
}

// Dynamic pricing: take the highest tier whose minimum quantity is met.
const tierPrice = (line: OrderLine): number => {
    const product = findLineProduct(line)
    if (!product?.price_tiers?.length) return 0

    const matched = product.price_tiers
        .filter(tier => line.quantity >= tier.min_quantity)
        .sort((a, b) => b.min_quantity - a.min_quantity)[0]

    return matched?.price ?? product.price_tiers[0].price
}

const linePrice = (line: OrderLine): number => line.price ?? tierPrice(line)
const lineTotal = (line: OrderLine): number => linePrice(line) * (line.quantity || 0)

const displayPrice = (line: OrderLine): number | null => {
    if (line.price !== null) return line.price

    return lineKey(line) ? tierPrice(line) : null
}

const updateLinePrice = (line: OrderLine, value: number | null) => {
    line.price = value ?? null
}

const useTierPrice = (line: OrderLine) => line.price = null

const isPriceOverridden = (line: OrderLine): boolean =>
    line.price !== null && line.price !== tierPrice(line)

const productsAmount = computed(() => form.products.reduce((sum, line) => sum + lineTotal(line), 0))
const totalAmount = computed(
    () => productsAmount.value
        + Number(form.shipping_cost || 0)
        - Number(form.shipping_subsidy || 0)
        + Number(form.additional_charge || 0)
)

const hasCustomProduct = computed(() => form.products.some(line => {
    const product = findLineProduct(line)
    return product?.print_type === 'ditakurnia' && !product.stock
}))

const brandDesigns = computed(() => brandData.value.designs)

const designProductLabel = (brandProductId: number | null) =>
    brandProductId ? findProduct(brandProductId)?.label : null

const designTypeShortcuts = ['Revisi', 'Tidak revisi', 'Desain pertama']

const estimationShortcuts = [3, 5, 7, 16, 20]

const estimationDate = (days: number): string => {
    const date = new Date(`${form.invoice_date}T00:00:00`)
    date.setDate(date.getDate() + days)

    return toDateInput(date)
}

const applyEstimation = (days: number) => form.estimated_finished_at = estimationDate(days)

const isFullShippingSubsidy = computed(
    () => Number(form.shipping_cost) > 0 && Number(form.shipping_subsidy) === Number(form.shipping_cost)
)

const applyFullShippingSubsidy = () => {
    form.shipping_subsidy = Number(form.shipping_cost || 0)
    form.clearErrors('shipping_subsidy')
}

watch(() => form.brand_id, () => {
    form.products = [emptyLine()]
    form.design_ids = []
    form.recipient_phone = ''
    form.clearErrors('brand_id')
    loadBrandData()
})

const lineError = (index: number, field: string): string | undefined =>
    (form.errors as Record<string, string>)[`products.${index}.${field}`]

const addLine = () => form.products.push(emptyLine())
const removeLine = (index: number) => form.products.splice(index, 1)

const isReloadingBrands = ref(false)
const reloadBrands = async () => {
    isReloadingBrands.value = true
    await loadBrandData()
    isReloadingBrands.value = false
}

const isPreviewOpen = ref(false)
const submit = () => {
    form[props.submitRoute.method ?? 'post'](route(props.submitRoute.name, props.submitRoute.parameters), {
        onSuccess: () => isPreviewOpen.value = false,
    })
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="cmx-auto xmax-w-5xl px-4 py-6 bg-gray-100">
        <section class="rounded-xl border border-gray-200 bg-white p-6">
            <h3 class="mb-4 font-semibold text-gray-700">Data pesanan</h3>

            <div v-if="canEditReference && order" class="mb-4">
                <label class="mb-1 block text-sm text-gray-600">Nama pesanan <span class="text-red-500">*</span></label>
                <InputText
                    v-model="form.reference"
                    class="w-full font-mono md:w-1/2"
                    :class="form.errors.reference ? 'errorShake' : ''"
                    :invalid="!!form.errors.reference"
                    @input="form.clearErrors('reference')"
                />
                <div class="mt-1 text-xs text-gray-400">Harus unik. Dicetak di resi pengiriman sebagai No. Invoice.</div>
                <div v-if="form.errors.reference" class="mt-1 text-xs text-red-500">{{ form.errors.reference }}</div>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Brand <span class="text-red-500">*</span></label>
                    <PureSelectInfiniteScroll
                        v-model="form.brand_id"
                        :fetchRoute="{ name: 'kasir.brands.options' }"
                        optionLabel="label"
                        optionValue="id"
                        :initialOption="initialBrand"
                        searchPlaceholder="Cari brand atau pelanggan"
                        placeholder="Pilih brand"
                        emptyText="Brand tidak ditemukan"
                        :invalid="!!form.errors.brand_id"
                        @change="brand => selectedBrand = brand as BrandOption | null"
                    >
                        <template #value="{ placeholder }">
                            <span v-if="selectedBrand" class="flex items-center gap-x-1.5">
                                {{ selectedBrand.label }}
                                <Icon v-if="selectedBrand.status === 'eksklusif'" :data="selectedBrand.status_icon" />
                            </span>
                            <span v-else>{{ placeholder }}</span>
                        </template>
                        <template #option="{ option }">
                            <span class="flex w-full items-center justify-between gap-x-3">
                                <span class="min-w-0">
                                    <span class="flex items-center gap-x-1.5">
                                        {{ option.label }}
                                        <Icon v-if="option.status === 'eksklusif'" :data="option.status_icon" />
                                    </span>
                                    <span v-if="option.customer_name" class="block truncate text-xs text-gray-400">{{ option.customer_name }}</span>
                                </span>
                                <span class="shrink-0 text-xs" :class="option.products_count ? 'text-gray-500' : 'text-gray-300'">
                                    {{ option.products_count ?? 0 }} produk
                                </span>
                            </span>
                        </template>
                    </PureSelectInfiniteScroll>
                    <div v-if="form.errors.brand_id" class="mt-1 text-xs text-red-500">{{ form.errors.brand_id }}</div>
                </div>
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Estimasi selesai</label>
                    <DatePicker
                        :modelValue="toDate(form.estimated_finished_at)"
                        dateFormat="dd/mm/yy"
                        placeholder="Pilih tanggal"
                        showIcon
                        iconDisplay="input"
                        showButtonBar
                        fluid
                        @update:modelValue="value => form.estimated_finished_at = value instanceof Date ? toDateInput(value) : ''"
                    />
                    <div class="mt-2 flex gap-x-2">
                        <button
                            v-for="days in estimationShortcuts"
                            :key="days"
                            type="button"
                            class="rounded-full border px-3 py-1 text-xs"
                            :class="form.estimated_finished_at === estimationDate(days)
                                ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                                : 'border-gray-200 text-gray-500 hover:bg-gray-50'"
                            @click="applyEstimation(days)"
                        >
                            {{ days }} hari
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="selectedBrand" class="mt-4 rounded-lg bg-gray-50 p-4 text-sm">
                <div class="grid gap-2 md:grid-cols-2">
                    <div><span class="text-gray-500">Pelanggan:</span> {{ selectedBrand.customer_name || '-' }}</div>
                    <div><span class="text-gray-500">No. telp:</span> {{ selectedBrand.phone || '-' }}</div>
                    <div><span class="text-gray-500">Status:</span> {{ selectedBrand.status_label || '-' }}</div>
                    <div><span class="text-gray-500">Tgl. invoice:</span> {{ useFormatTime(form.invoice_date) }}</div>
                </div>
                <div class="mt-3 border-t border-gray-200 pt-3">
                    <label class="mb-1 block text-gray-600">No. penerima untuk pesanan ini</label>
                    <InputText
                        v-model="form.recipient_phone"
                        :placeholder="selectedBrand.recipient_phone || '08xxxxxxxxxx'"
                        :invalid="!!form.errors.recipient_phone"
                        class="w-full md:w-1/2"
                    />
                    <div class="mt-1 text-xs text-gray-400">
                        Kosongkan untuk memakai no. penerima brand ({{ selectedBrand.recipient_phone || '-' }}). Dicetak di resi pengiriman.
                    </div>
                    <div v-if="form.errors.recipient_phone" class="mt-1 text-xs text-red-500">{{ form.errors.recipient_phone }}</div>
                </div>
                <div v-if="selectedBrand.special_note" class="mt-2 rounded bg-amber-50 p-2 text-amber-800">
                    Catatan khusus: {{ selectedBrand.special_note }}
                </div>
            </div>

            <div class="mt-4">
                <label class="mb-1 block text-sm text-gray-600">Catatan:</label>
                <Textarea v-model="form.note" rows="2" autoResize placeholder="Masukkan catatan untuk pesanan ini..." fluid />
                <div class="mt-1 text-xs text-gray-400">Catatan internal, tidak ditampilkan di invoice pelanggan.</div>
            </div>
        </section>

        <section class="mt-4 rounded-xl border border-gray-200 bg-white p-6">
            <div class="mb-4 flex items-center justify-between">
                <h3 class="font-semibold text-gray-700">Produk</h3>
                <Button size="xs" type="tertiary" icon="fal fa-plus" label="Tambah produk" @click="addLine" />
            </div>

            <div v-if="isLoadingBrandData" class="flex flex-col gap-3">
                <div v-for="index in Math.max(form.products.length, 1)" :key="`skeleton-${index}`" class="grid items-end gap-3 md:grid-cols-12">
                    <Skeleton class="md:col-span-5" height="2.5rem" />
                    <Skeleton class="md:col-span-2" height="2.5rem" />
                    <Skeleton class="md:col-span-2" height="2.5rem" />
                    <Skeleton class="md:col-span-2" height="1.5rem" />
                </div>
            </div>

            <div v-else-if="!productOptions.length" class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                Brand ini belum punya produk.
                <template v-if="selectedBrand">
                    <a
                        :href="route('kasir.brands.products.create', selectedBrand.id)"
                        target="_blank"
                        rel="noopener"
                        class="text-blue-500 underline"
                    >Tambah produk brand</a>,
                    lalu
                    <button type="button" class="text-blue-500 underline" :disabled="isReloadingBrands" @click="reloadBrands">
                        {{ isReloadingBrands ? 'memuat...' : 'muat ulang daftar produk' }}
                    </button>.
                </template>
            </div>

            <div v-else class="flex flex-col gap-3">
                <div v-for="(line, index) in form.products" :key="`line-${index}`" class="grid items-start gap-3 md:grid-cols-12">
                    <div class="md:col-span-5">
                        <label class="mb-1 block text-xs text-gray-500">Produk</label>
                        <Select
                            :modelValue="lineKey(line)"
                            :options="productOptions"
                            optionLabel="label"
                            optionValue="key"
                            filter
                            filterPlaceholder="Cari produk"
                            placeholder="Pilih produk"
                            :invalid="!!lineError(index, 'brand_product_id')"
                            fluid
                            @update:modelValue="key => { selectLineProduct(line, key); form.clearErrors(`products.${index}.brand_product_id` as any) }"
                        >
                            <template #option="{ option }">
                                <div class="flex w-full items-center justify-between gap-x-3">
                                    <span>{{ option.label }}</span>
                                    <span v-if="option.stock" class="shrink-0 text-xs" :class="option.stock.available > 0 ? 'text-gray-400' : 'text-red-500'">
                                        Stok {{ useFormatNumber(option.stock.available) }}
                                    </span>
                                </div>
                            </template>
                        </Select>
                        <div class="mt-1 text-xs text-gray-400">
                            {{ findLineProduct(line)?.category }}
                            <span :class="findLineProduct(line)?.print_label ? '' : 'opacity-0'">
                                &middot; {{ findLineProduct(line)?.print_label }}
                            </span>
                        </div>
                        <div v-if="lineStock(line)" class="mt-0.5 text-xs" :class="isOverStock(line) ? 'text-red-500' : 'text-cyan-700'">
                            Diambil dari {{ lineStock(line)?.is_general ? 'stok umum' : 'stok brand' }},
                            tersedia {{ useFormatNumber(lineStock(line)?.available ?? 0) }} pcs.
                            <a
                                v-if="isOverStock(line)"
                                :href="route('kasir.stocks.show', lineStock(line)?.id)"
                                target="_blank"
                                rel="noopener"
                                class="font-medium underline"
                            >Tambah / produksi stok</a>
                        </div>
                        <div v-if="lineError(index, 'brand_product_id')" class="mt-1 text-xs text-red-500">{{ lineError(index, 'brand_product_id') }}</div>
                    </div>
                    <div class="md:col-span-2">
                        <label class="mb-1 block text-xs text-gray-500">Jumlah</label>
                        <InputNumber
                            :modelValue="line.quantity"
                            locale="id-ID"
                            :min="0"
                            fluid
                            :class="lineError(index, 'quantity') ? 'errorShake' : ''"
                            :invalid="!!lineError(index, 'quantity')"
                            @update:modelValue="value => { line.quantity = Number(value ?? 0); form.clearErrors(`products.${index}.quantity` as any) }"
                        />
                        <div class="mt-1 text-xs text-red-500"
                            :class="lineError(index, 'quantity') ? '' : 'invisible'"
                        >{{ lineError(index, 'quantity') ?? '&nbsp' }}</div>
                    </div>
                    <div class="md:col-span-2">
                        <label class="mb-1 block text-xs text-gray-500">Harga / pcs</label>
                        <InputNumber
                            :modelValue="displayPrice(line)"
                            mode="currency"
                            currency="IDR"
                            locale="id-ID"
                            :maxFractionDigits="0"
                            :min="0"
                            fluid
                            @update:modelValue="value => updateLinePrice(line, value)"
                        />
                        <div
                            class="mt-1 flex flex-wrap items-center gap-x-2 text-xs"
                            :class="isPriceOverridden(line) ? '' : 'invisible'"
                        >
                            <span class="text-gray-400 line-through">{{ useFormatCurrency(tierPrice(line) ?? 0) }}</span>
                            <button type="button" class="text-cyan-600 hover:underline" @click="useTierPrice(line)">
                                Pakai harga asli
                            </button>
                        </div>
                    </div>
                    <div class="md:col-span-2 pb-2 text-right text-sm font-medium self-end">
                        {{ useFormatCurrency(lineTotal(line)) }}
                    </div>
                    <div class="md:col-span-1 pb-2 text-right">
                        <button v-if="form.products.length > 1" class="text-gray-400 hover:text-red-500" @click="removeLine(index)">
                            <FontAwesomeIcon icon="fal fa-times" fixed-width aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="form.errors.products" class="mt-2 text-xs text-red-500">{{ form.errors.products }}</div>

            <div v-if="hasCustomProduct" class="mt-4 rounded bg-amber-100 p-3 text-sm text-amber-800">
                Pesanan ini memuat produk cetak di Dita Kurnia, jadi akan menjadi order custom yang perlu diproduksi.
            </div>
        </section>

        <section class="mt-4 rounded-xl border border-gray-200 bg-white p-6">
            <h3 class="mb-4 font-semibold text-gray-700">Desain</h3>
            <div class="mb-4">
                <label class="mb-1 block text-sm text-gray-600">Tipe desain</label>
                <InputText v-model="form.design_type" placeholder="Contoh: Revisi 18 Agustus" :invalid="!!form.errors.design_type" fluid />
                <div class="mt-2 flex flex-wrap gap-2">
                    <button
                        v-for="designType in designTypeShortcuts"
                        :key="designType"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="form.design_type === designType
                            ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                            : 'border-gray-200 text-gray-500 hover:bg-gray-50'"
                        @click="form.design_type = designType"
                    >
                        {{ designType }}
                    </button>
                </div>
                <div class="mt-1 text-xs text-gray-400">Ditampilkan ke staff Manufacture bersama link desain.</div>
                <div v-if="form.errors.design_type" class="mt-1 text-xs text-red-500">{{ form.errors.design_type }}</div>
            </div>
            <div v-if="isLoadingBrandData" class="grid gap-2 md:grid-cols-2">
                <Skeleton v-for="index in 2" :key="`design-skeleton-${index}`" height="4.5rem" />
            </div>
            <div v-else-if="!brandDesigns.length" class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                Belum ada desain untuk brand yang dipilih.
            </div>
            <div v-else class="grid gap-2 md:grid-cols-2">
                <label
                    v-for="design in brandDesigns"
                    :key="design.id"
                    class="flex cursor-pointer items-start gap-x-2 rounded-lg border border-gray-200 p-3 text-sm hover:bg-gray-50"
                >
                    <Checkbox v-model="form.design_ids" :value="design.id" class="mt-0.5" />
                    <span>
                        <span class="block font-medium">{{ design.label }}</span>
                        <span v-if="designProductLabel(design.brand_product_id)" class="block text-xs text-gray-500">
                            {{ designProductLabel(design.brand_product_id) }}
                        </span>
                        <span class="block truncate text-xs text-gray-400">{{ design.link }}</span>
                        <span class="block text-xs text-gray-400">
                            Terakhir diupdate {{ useFormatTime(design.link_updated_at ?? undefined) }}
                        </span>
                    </span>
                </label>
            </div>
        </section>

        <section class="mt-4 rounded-xl border border-gray-200 bg-white p-6">
            <h3 class="mb-4 font-semibold text-gray-700">Tagihan</h3>
            <div class="grid gap-4 md:grid-cols-4">
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Ongkir</label>
                    <InputNumber
                        :modelValue="form.shipping_cost"
                        mode="currency"
                        currency="IDR"
                        locale="id-ID"
                        :maxFractionDigits="0"
                        :min="0"
                        fluid
                        @update:modelValue="value => form.shipping_cost = Number(value ?? 0)"
                    />
                </div>
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Subsidi ongkir</label>
                    <InputNumber
                        :modelValue="form.shipping_subsidy"
                        mode="currency"
                        currency="IDR"
                        locale="id-ID"
                        :maxFractionDigits="0"
                        :min="0"
                        fluid
                        :class="form.errors.shipping_subsidy ? 'errorShake' : ''"
                        :invalid="!!form.errors.shipping_subsidy"
                        @update:modelValue="value => { form.shipping_subsidy = Number(value ?? 0); form.clearErrors('shipping_subsidy') }"
                    />
                    <button
                        type="button"
                        class="mt-1 text-xs text-cyan-600 underline disabled:cursor-default disabled:opacity-40"
                        :disabled="isFullShippingSubsidy || !Number(form.shipping_cost)"
                        @click="applyFullShippingSubsidy"
                    >
                        Sama dengan ongkir
                    </button>
                    <div v-if="form.errors.shipping_subsidy" class="mt-1 text-xs text-red-500">{{ form.errors.shipping_subsidy }}</div>
                    <div v-else class="mt-1 text-xs text-gray-400">Mengurangi ongkir, maksimal sebesar ongkir.</div>
                </div>
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Biaya tambahan</label>
                    <InputNumber
                        :modelValue="form.additional_charge"
                        mode="currency"
                        currency="IDR"
                        locale="id-ID"
                        :maxFractionDigits="0"
                        :min="0"
                        fluid
                        @update:modelValue="value => form.additional_charge = Number(value ?? 0)"
                    />
                </div>
                <div>
                    <label class="mb-1 block text-sm text-gray-600">Keterangan biaya tambahan</label>
                    <InputText v-model="form.additional_charge_note" placeholder="Request khusus customer" fluid />
                    <div class="mt-1 text-xs text-gray-400">Biaya tambahan dan keterangannya ditampilkan di invoice pelanggan.</div>
                </div>
            </div>

            <dl class="mt-4 divide-y divide-gray-100 border-t border-gray-100 text-sm">
                <div class="flex justify-between py-2">
                    <dt class="text-gray-500">Total produk</dt>
                    <dd class="font-medium">{{ useFormatCurrency(productsAmount) }}</dd>
                </div>
                <div class="flex justify-between py-2">
                    <dt class="text-gray-500">Ongkir</dt>
                    <dd class="font-medium">{{ useFormatCurrency(Number(form.shipping_cost || 0)) }}</dd>
                </div>
                <div v-if="form.shipping_subsidy" class="flex justify-between py-2">
                    <dt class="text-gray-500">Subsidi ongkir</dt>
                    <dd class="font-medium text-emerald-600">-{{ useFormatCurrency(Number(form.shipping_subsidy || 0)) }}</dd>
                </div>
                <div class="flex justify-between py-2">
                    <dt class="text-gray-500">Biaya tambahan</dt>
                    <dd class="font-medium">{{ useFormatCurrency(Number(form.additional_charge || 0)) }}</dd>
                </div>
                <div class="flex justify-between py-3 text-base">
                    <dt class="font-semibold">Total tagihan</dt>
                    <dd class="font-bold">{{ useFormatCurrency(totalAmount) }}</dd>
                </div>
            </dl>
        </section>

        <div class="mt-4 flex justify-end gap-x-3">
            <Button type="tertiary" label="Preview & checkout" @click="isPreviewOpen = true" />
            <Button type="save" :loading="form.processing" label="Simpan" @click="submit" />
        </div>
    </div>

    <Modal :isOpen="isPreviewOpen" @onClose="isPreviewOpen = false" width="w-3/4 max-w-2xl">
        <div class="p-2">
            <h3 class="mb-4 text-lg font-semibold">Preview pesanan</h3>

            <dl class="mb-4 grid gap-2 text-sm md:grid-cols-2">
                <div><span class="text-gray-500">Brand:</span> {{ selectedBrand?.label }}</div>
                <div><span class="text-gray-500">Pelanggan:</span> {{ selectedBrand?.customer_name || '-' }}</div>
                <div><span class="text-gray-500">Tgl. invoice:</span> {{ form.invoice_date }}</div>
                <div><span class="text-gray-500">Estimasi selesai:</span> {{ form.estimated_finished_at || '-' }}</div>
                <div><span class="text-gray-500">Tipe desain:</span> {{ form.design_type || '-' }}</div>
            </dl>

            <table class="w-full text-sm">
                <thead class="text-xs uppercase text-gray-400">
                    <tr>
                        <th class="py-2 text-left">Produk</th>
                        <th class="py-2 text-right">Qty</th>
                        <th class="py-2 text-right">Harga</th>
                        <th class="py-2 text-right">Subtotal</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <tr v-for="(line, index) in form.products" :key="`preview-${index}`">
                        <td class="py-2">{{ findLineProduct(line)?.label || '-' }}</td>
                        <td class="py-2 text-right">{{ line.quantity }}</td>
                        <td class="py-2 text-right">{{ useFormatCurrency(linePrice(line)) }}</td>
                        <td class="py-2 text-right">{{ useFormatCurrency(lineTotal(line)) }}</td>
                    </tr>
                </tbody>
                <tfoot class="border-t border-gray-200">
                    <tr>
                        <td colspan="3" class="py-2 text-right text-gray-500">Ongkir</td>
                        <td class="py-2 text-right">{{ useFormatCurrency(Number(form.shipping_cost || 0)) }}</td>
                    </tr>
                    <tr v-if="form.shipping_subsidy">
                        <td colspan="3" class="py-2 text-right text-gray-500">Subsidi ongkir</td>
                        <td class="py-2 text-right text-emerald-600">-{{ useFormatCurrency(Number(form.shipping_subsidy || 0)) }}</td>
                    </tr>
                    <tr>
                        <td colspan="3" class="py-2 text-right text-gray-500">Biaya tambahan</td>
                        <td class="py-2 text-right">{{ useFormatCurrency(Number(form.additional_charge || 0)) }}</td>
                    </tr>
                    <tr>
                        <td colspan="3" class="py-2 text-right font-semibold">Total</td>
                        <td class="py-2 text-right font-bold">{{ useFormatCurrency(totalAmount) }}</td>
                    </tr>
                </tfoot>
            </table>

            <div class="mt-4 flex justify-end gap-x-3">
                <Button type="tertiary" label="Kembali" @click="isPreviewOpen = false" />
                <Button type="save" :loading="form.processing" label="Submit pesanan" @click="submit" />
            </div>
        </div>
    </Modal>
</template>
